// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import { useTheme } from '@mui/material/styles';

// ** Components
import Section from '@/components/common/Section';
import NewsGrid from './NewsGrid';
import NewsCard from './NewsCard';
import ComponentBlocksSearchFilter from '@/components/strapi/Shared/ComponentBlocksSearchFilter';

// ** Icons
import SearchOffIcon from '@mui/icons-material/SearchOff';

// ** Utils
import { useGetNews, useGetNewsPeriods, useGetNewsCategories } from '@/lib/utils/newsDataUtils';

// ** Hooks
import { useSearchLogic } from '@/hooks/useSearchLogic';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type {
	NewsFiltersInput,
	StringFilterInput,
	PublicationStatus,
	DateTimeFilterInput,
	QueryAllNews_ConnectionArgs,
	ComponentBlocksNewsBlock as BlocksNewsBlockProps
} from '@/types/graphql';

type ComponentBlocksNewsBlockProps = StrapiComponentProps<BlocksNewsBlockProps>;
type NewsFiltersExtended = NewsFiltersInput & {
	newsManualPublishedDate?: StringFilterInput;
	// This allows the object to be flexible while keeping known types safe
	[key: string]: unknown;
};

export type SearchFilterConfig = {
	searchFilterShowCategory?: boolean | null;
	searchFilterShowPeriod?: boolean | null;
	searchFilterShowSearch?: boolean | null;
	searchFilterCategoryPlaceholder?: string | null;
	searchFilterPeriodPlaceholder?: string | null;
	searchFilterSearchPlaceholder?: string | null;
	// ... any other fields
};

/**
 * ComponentBlocksNewsBlockProps
 * * @param {string} locale - Current language
 * @param {number} newsPerPage - Pagination limit from CMS
 * @param {object} newsSearchFilters - The Strapi config object for placeholders/visibility
 */
const ComponentBlocksNewsBlock = ({
	host,
	locale,
	filterTools,
	newsPerPage,
	containerSx,
	newsSearchFilters,
	...props
}: ComponentBlocksNewsBlockProps) => {
	const theme = useTheme();
	const sp = theme.spacing;
	const sectionRef = React.useRef<HTMLDivElement>(null);

	const { searchState, searchHandlers } = useSearchLogic();
	const { handlePageChange } = searchHandlers;

	const filters = {} as NewsFiltersExtended;

	if (searchState.selectedCategory !== 'all') {
		filters.newsCategory = {
			eq: searchState.selectedCategory
		} as StringFilterInput;
	}

	if (searchState.searchTerm) {
		filters.newsTitle = {
			containsi: searchState.searchTerm
		} as StringFilterInput;
	}

	if (searchState.selectedPeriod && searchState.selectedPeriod !== 'all') {
		const year = searchState.selectedPeriod;
		filters.publishedAt = {
			gte: `${year}-01-01`,
			lte: `${year}-12-31`
		} as DateTimeFilterInput;
	}

	const pageSize = newsPerPage || 6;

	const queryVariables: QueryAllNews_ConnectionArgs = {
		locale: locale,
		pagination: {
			page: searchState.page,
			pageSize,
			limit: null,
			start: null
		},
		filters: filters,
		sort: ['publishedAt:desc'],
		status: 'PUBLISHED' as PublicationStatus,
		hasPublishedVersion: true,
		publicationFilter: 'HAS_PUBLISHED_VERSION'
	};

	const { allNews_connection, loading } = useGetNews(queryVariables);
	const { categories, loading: catsLoading } = useGetNewsCategories({
		locale,
		allLabel: newsSearchFilters?.searchFilterCategoryPlaceholder
	});
	const { periods } = useGetNewsPeriods({ locale });

	const nodes = (allNews_connection?.nodes || []) as Array<
		NonNullable<NonNullable<typeof allNews_connection>['nodes']>[number]
	>;
	const totalCount = (allNews_connection as any)?.pageInfo?.total ?? nodes.length;
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const dynamicPath = props?.newsBasePath;

	React.useEffect(() => {
		if (!loading && searchState.page > totalPages) {
			handlePageChange(1);
		}
	}, [loading, searchState.page, totalPages, handlePageChange]);

	const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		handlePageChange(value);

		if (sectionRef.current) {
			window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: true }));

			const navbarEl = document.querySelector<HTMLElement>('[class*="MuiAppBar"]');
			const navbarHeight = navbarEl?.offsetHeight ?? 100;
			const elementTop = sectionRef.current.getBoundingClientRect().top + window.scrollY;

			window.scrollTo({
				top: Math.max(0, elementTop - navbarHeight - 16),
				behavior: 'smooth'
			});

			setTimeout(() => {
				window.dispatchEvent(new CustomEvent('programmatic-scroll', { detail: false }));
			}, 1000);
		}
	};

	const searchFilterConfig = newsSearchFilters
		? ({
				...newsSearchFilters,
				searchFilterShowCategory: newsSearchFilters.searchFilterShowCategory ?? undefined,
				searchFilterShowPeriod: newsSearchFilters.searchFilterShowPeriod ?? undefined,
				searchFilterShowSearch: newsSearchFilters.searchFilterShowSearch ?? undefined,
				searchFilterCategoryPlaceholder: newsSearchFilters.searchFilterCategoryPlaceholder ?? undefined,
				searchFilterPeriodPlaceholder: newsSearchFilters.searchFilterPeriodPlaceholder ?? undefined,
				searchFilterSearchPlaceholder: newsSearchFilters.searchFilterSearchPlaceholder ?? undefined
			} as React.ComponentProps<typeof ComponentBlocksSearchFilter>['config'])
		: undefined;

	return (
		<Section containerSx={containerSx}>
			<Box ref={sectionRef} />
			{filterTools && (
				<Box sx={{ mt: { xs: sp(8), md: sp(16) } }}>
					<ComponentBlocksSearchFilter
						config={searchFilterConfig}
						options={{
							categories: categories,
							periods: periods
						}}
						state={searchState}
						handlers={searchHandlers}
						backgroundColor={theme.palette.heroLightBackground?.main as string}
					/>
				</Box>
			)}

			{(loading || catsLoading) && (
				<Box sx={{ py: 10, textAlign: 'center', width: '100%' }}>
					<CircularProgress color="primary" size={40} />
				</Box>
			)}

			<NewsGrid sx={{ mt: { xs: sp(8), md: sp(16) } }}>
				{nodes.map((card) => {
					const { publishedAt, createdAt, newsTitle, newsDescription, newsBild, ...rest } = card;

					return (
						<NewsCard
							basePath={dynamicPath ?? ''}
							key={card.slug}
							host={host}
							title={newsTitle}
							description={newsDescription}
							imageData={newsBild}
							publishedAt={publishedAt ? String(publishedAt) : undefined}
							{...rest}
						/>
					);
				})}
			</NewsGrid>

			{!loading && !catsLoading && nodes.length === 0 && (
				<Box
					sx={{
						py: 10,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 2,
						color: 'text.secondary'
					}}
				>
					<SearchOffIcon sx={{ fontSize: 56, opacity: 0.4 }} />
					<Typography variant="body-lg" sx={{ fontWeight: 500 }}>
						Keine Beiträge gefunden.
					</Typography>
					<Typography variant="body-md" sx={{ opacity: 0.7 }}>
						Bitte passen Sie Ihre Suche oder Filter an.
					</Typography>
				</Box>
			)}

			{!loading && !catsLoading && totalPages > 1 && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						mt: { xs: sp(8), md: sp(12) }
					}}
				>
					<Pagination
						page={searchState.page}
						count={totalPages}
						shape="rounded"
						siblingCount={1}
						onChange={handlePaginationChange}
						aria-label="Nachrichten-Seitennavigation"
					/>
				</Box>
			)}
		</Section>
	);
};

export default ComponentBlocksNewsBlock;
